import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { DesignSystemPrimitive, PrimitiveCategory } from 'designSystem/types';
import { PrimitiveCard } from './primitives/PrimitiveCard';
import FilterPills from './FilterPills';
import SearchBar from './SearchBar';
import * as S from './styles';
import * as PrimitiveS from './primitives/styles'; // Renamed to avoid conflict with section styles

// Map PrimitiveCategory to display name
const categoryDisplayNames: Record<PrimitiveCategory, string> = {
	button: 'Buttons',
	card: 'Cards',
	form: 'Forms',
	layout: 'Layout',
	typography: 'Typography',
	navigation: 'Navigation',
	other: 'Other',
};

const StylesView = () => {
	const [selectedPrimitiveId, setSelectedPrimitiveId] = useState<string | null>(null);
	const designSystemContent = useSelector((r: RootReducer) => r.mode.designSystemContent);
	const { selectedCategories, searchQuery } = useSelector(
		(r: RootReducer) => r.mode.designSystemFilters.styles
	);

	// Get available categories with counts
	const availableCategories = useMemo(() => {
		if (!designSystemContent?.primitives) return [];
		return (Object.keys(categoryDisplayNames) as PrimitiveCategory[]).map((category) => ({
			value: category,
			label: categoryDisplayNames[category],
			count: designSystemContent.primitives!.byCategory[category]?.length || 0,
		}));
	}, [designSystemContent?.primitives]);

	// Filter primitives based on selected categories and search query
	const primitivesToDisplay = useMemo(() => {
		if (!designSystemContent?.primitives) return [];

		let primitives: DesignSystemPrimitive[] = [];

		// If categories selected, show union of all selected categories
		if (selectedCategories.length > 0) {
			const primitiveSets = selectedCategories.map(
				(cat) => designSystemContent.primitives!.byCategory[cat] || []
			);
			// Union: combine all sets and remove duplicates
			const primitiveMap = new Map<string, DesignSystemPrimitive>();
			primitiveSets.forEach((primitiveSet) => {
				primitiveSet.forEach((primitive) => {
					primitiveMap.set(primitive.id, primitive);
				});
			});
			primitives = Array.from(primitiveMap.values());
		} else {
			// No categories selected, show all
			primitives = designSystemContent.primitives.all || [];
		}

		// Apply search filter
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase();
			primitives = primitives.filter(
				(primitive) =>
					primitive.name.toLowerCase().includes(query) ||
					primitive.id.toLowerCase().includes(query) ||
					primitive.baseSelector.toLowerCase().includes(query)
			);
		}

		return primitives;
	}, [designSystemContent?.primitives, selectedCategories, searchQuery]);

	return (
		<S.SectionBase>
			<S.SectionHeader>
				<S.SectionTitle>Primitives</S.SectionTitle>
				<SearchBar section="styles" placeholder="Search primitives..." />
			</S.SectionHeader>
			<S.SectionContent>
				<FilterPills section="styles" categories={availableCategories} />
				{primitivesToDisplay.length === 0 ? (
					<p>
						No primitives found
						{selectedCategories.length > 0 && ` matching selected categories`}
						{searchQuery && ` matching "${searchQuery}"`}.
					</p>
				) : (
					<PrimitiveS.PrimitiveGrid>
						{primitivesToDisplay.map((primitive) => (
							<PrimitiveCard
								key={primitive.id}
								primitive={primitive}
								selected={selectedPrimitiveId === primitive.id}
								onClick={() =>
									setSelectedPrimitiveId(primitive.id === selectedPrimitiveId ? null : primitive.id)
								}
							/>
						))}
					</PrimitiveS.PrimitiveGrid>
				)}
			</S.SectionContent>
		</S.SectionBase>
	);
};

export default StylesView;
